import { useMemo } from 'react';

import { useShowMore } from 'hooks';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { List } from 'components/list';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { ButtonWithRef } from 'components/button';
import { ListHeader } from 'components/list-header';
import { formatTimezoneSpecific } from 'utils/date';

import * as Styles from './styles';
import { RevenueProps } from './types';
import { PopupList } from './popup-list';
import {
  titles,
  template,
  usersTitles,
  getTotalsInfo,
  employeeTitles,
  tabletTemplate,
  commonButtonProps,
} from './config';

export const Revenue = ({ data, event }: RevenueProps) => {
  const { visibleData, ...showMoreProps } = useShowMore(data?.report);

  const totalsInfo = useMemo(
    () => data?.total && getTotalsInfo(data?.total),
    [data?.total]
  );

  return (
    <List
      {...showMoreProps}
      count={visibleData?.length}
      mainWrapperStyles={{ padding: 0 }}
      header={<ListHeader padding="8px" titles={titles} template={template} />}
      topHeader={
        <Styles.TotalContainer>
          {totalsInfo?.map(({ title, value }, index) => (
            <Styles.StyledInfoCard
              key={index}
              title={title}
              value={formatPrice(value)}
            />
          ))}
        </Styles.TotalContainer>
      }
    >
      {visibleData?.map((value, index) => (
        <ListCard
          key={index}
          headers={titles}
          template={template}
          values={[
            formatTimezoneSpecific(value?.checkoutDate, event?.timezone?.code),
            formatPrice(value?.paymentMethods?.check),
            formatPrice(value?.paymentMethods?.cash),
            formatPrice(value?.paymentMethods?.credit),
            formatPrice(value?.total?.tip),
            formatPrice(value?.total?.tax),
            formatPrice(value?.total?.processingFees),
            formatPrice(value?.total?.shippingPrices),
            formatPrice(value?.total?.total),
            <Flex gap={32}>
              <PopupList
                headerTitles={employeeTitles}
                count={value?.employees?.length}
                trigger={(open: boolean) => (
                  <ButtonWithRef
                    {...commonButtonProps}
                    icon={open ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                  />
                )}
                elements={value?.employees?.map((employee, i) => (
                  <ListCard
                    key={i}
                    headers={employeeTitles}
                    tabletTemplate={tabletTemplate}
                    values={[
                      employee.name,
                      formatPrice(employee.grossSaleContribution),
                    ]}
                  />
                ))}
              />

              <PopupList
                headerTitles={usersTitles}
                count={value?.users?.length}
                trigger={
                  <ButtonWithRef {...commonButtonProps} icon={<Icon.Info />} />
                }
                elements={value.users.map((user) => (
                  <ListCard
                    key={user.userId}
                    headers={usersTitles}
                    tabletTemplate={tabletTemplate}
                    values={[user.name, user.createdCheckout]}
                  />
                ))}
              />
            </Flex>,
          ]}
        />
      ))}
    </List>
  );
};
